import React, { useState, useEffect } from 'react';
import { saleService, Sale, NewSale } from '../services/saleService';
import { clientService, Client } from '../services/clientService';
import { utilityService } from '../services/utilityService';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Container, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';

const Sales: React.FC = () => {
  const [sales, setSales] = useState<Sale[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingSale, setEditingSale] = useState<Sale | null>(null);
  const [formData, setFormData] = useState({
    clientId: '',
    clientName: '',
    quantity: '',
    pricePerLiter: '',
    date: new Date().toISOString()
  });

  // Generate array of prices from 45 to 60
  const priceOptions = Array.from({ length: 16 }, (_, i) => (i + 45).toString());

  const { currentUser, userProfile, selectedAgent } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    loadData();
  }, [currentUser, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      
      if (!userProfile) {
        setError('User profile not available');
        return;
      }
      
      const [salesData, clientsData] = await Promise.all([
        saleService.getSales(),
        clientService.getClients(userProfile.role, selectedAgent || undefined)
      ]);
      setSales(salesData);
      setClients(clientsData);
    } catch (err) {
      console.error('Error loading data:', err);
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to load data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      clientId: '',
      clientName: '',
      quantity: '',
      pricePerLiter: '',
      date: new Date().toISOString()
    });
    setEditingSale(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const selectedClient = clients.find(c => c.id === formData.clientId);
    if (!selectedClient) {
      setError('Please select a client');
      return;
    }

    try {
      // Convert string inputs to numbers, preserving whole numbers
      const quantity = Number(formData.quantity);
      const pricePerLiter = Number(formData.pricePerLiter);

      const saleData: NewSale = {
        clientId: selectedClient.id!,
        clientName: selectedClient.name,
        quantity,
        pricePerLiter,
        date: formData.date
      };

      if (editingSale) {
        // Update existing sale
        await saleService.updateSale(editingSale.id!, saleData);
        setSales(prevSales => 
          prevSales.map(sale => 
            sale.id === editingSale.id 
              ? { ...sale, ...saleData, total: saleData.quantity * saleData.pricePerLiter }
              : sale
          )
        );
      } else {
        // Add new sale
        const newSale = await saleService.addSale(saleData);
        setSales(prevSales => [newSale, ...prevSales]);
      }
      setShowForm(false);
      resetForm();
    } catch (error) {
      console.error('Error saving sale:', error);
      setError('Failed to save sale. Please try again.');
    }
  };

  const handleEdit = (sale: Sale) => {
    setEditingSale(sale);
    setFormData({
      clientId: sale.clientId,
      clientName: sale.clientName,
      quantity: sale.quantity.toString(),
      pricePerLiter: sale.pricePerLiter.toString(),
      date: sale.date
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this sale?')) {
      try {
        setError('');
        await saleService.deleteSale(id);
        setSales(prevSales => prevSales.filter(sale => sale.id !== id));
      } catch (err) {
        console.error('Error deleting sale:', err);
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('Failed to delete sale. Please try again.');
        }
      }
    }
  };

  const handleClientChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const clientId = e.target.value;
    const client = clients.find(c => c.id === clientId);
    if (client) {
      setFormData({
        ...formData,
        clientId: client.id!,
        clientName: client.name
      });
    }
  };

  const handleExport = () => {
    utilityService.exportSalesToCSV(sales);
  };

  if (!currentUser) {
    return null;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <div className="page-header">
          <h1>Sales</h1>
          <div className="header-actions">
            <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
              {showForm ? 'Cancel' : 'Add New Sale'}
            </button>
            <button className="btn btn-secondary" onClick={handleExport}>
              Export to CSV
            </button>
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        <Dialog 
          open={showForm} 
          onClose={() => setShowForm(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {editingSale ? 'Edit Sale' : 'Add New Sale'}
          </DialogTitle>
          <DialogContent>
            <form onSubmit={handleSubmit} style={{ marginTop: '1rem' }}>
              <div className="form-group">
                <label className="form-label">Client</label>
                <select
                  className="form-input"
                  value={formData.clientId}
                  onChange={handleClientChange}
                  required
                >
                  <option value="">Select a client</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Quantity (L)</label>
                <input
                  type="number"
                  step="1"
                  className="form-input"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Price per Liter (KES)</label>
                <select
                  className="form-input"
                  value={formData.pricePerLiter}
                  onChange={(e) => setFormData({ ...formData, pricePerLiter: e.target.value })}
                  required
                >
                  <option value="">Select price per liter</option>
                  {priceOptions.map((price) => (
                    <option key={price} value={price}>
                      {price}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Date</label>
                <input
                  type="datetime-local"
                  className="form-input"
                  value={formData.date.slice(0, 16)}
                  onChange={(e) => setFormData({ ...formData, date: new Date(e.target.value).toISOString() })}
                  required
                />
              </div>
            </form>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained" color="primary">
              {editingSale ? 'Update Sale' : 'Add Sale'}
            </Button>
          </DialogActions>
        </Dialog>

        {loading ? (
          <div className="loading">Loading sales...</div>
        ) : (
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Client Name</th>
                  <th>Quantity (L)</th>
                  <th>Price per Liter (KES)</th>
                  <th>Total (KES)</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sales.map((sale) => (
                  <tr key={sale.id}>
                    <td>{sale.clientName}</td>
                    <td>{sale.quantity.toFixed(2)}</td>
                    <td>KES {sale.pricePerLiter.toLocaleString()}</td>
                    <td>KES {sale.total.toLocaleString()}</td>
                    <td>{utilityService.formatDate(sale.date)}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleEdit(sale)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(sale.id!)}
                        style={{ marginLeft: '0.5rem' }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Paper>
    </Container>
  );
};

export default Sales; 